import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, Trash2, ChevronRight, ChevronLeft } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useError } from '../../contexts/ErrorContext';

const ErrorSidebar = () => {
    const { errors, isOpen, toggleSidebar, removeError, clearErrors } = useError();
    const location = useLocation();

    // Define allowed paths prefixes where the sidebar should be visible
    const allowedPathPrefixes = ['/dashboard', '/tds-dashboard', '/gst-dashboard'];

    // Check if current path starts with any of the allowed prefixes
    const isVisible = allowedPathPrefixes.some(prefix => location.pathname.startsWith(prefix));

    if (!isVisible) return null;

    return (
        <>
            {/* Toggle Button - Middle Right */}
            <motion.button
                onClick={toggleSidebar}
                className="fixed right-0 top-1/2 -translate-y-1/2 z-[60] bg-red-600 text-white py-4 px-1 rounded-l-md shadow-lg flex flex-col items-center justify-center cursor-pointer hover:bg-red-700 transition-colors"
                initial={{ x: 0 }}
                animate={{ x: isOpen ? -320 : 0 }} // Move button left when sidebar opens
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                title={isOpen ? "Close Errors" : "Show Errors"}
            >
                <div className="writing-vertical-lr transform rotate-180 font-bold text-sm tracking-widest flex items-center gap-2">
                    <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>ERRORS</span>
                    {errors.length > 0 && (
                        <span className="bg-white text-red-600 text-[10px] font-bold px-1 rounded-full w-5 h-5 flex items-center justify-center transform rotate-90">
                            {errors.length}
                        </span>
                    )}
                </div>
                {isOpen ? <ChevronRight className="w-4 h-4 mt-2" /> : <ChevronLeft className="w-4 h-4 mt-2" />}
            </motion.button>

            {/* Sidebar Panel */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ x: 320 }}
                        animate={{ x: 0 }}
                        exit={{ x: 320 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        className="fixed right-0 top-0 h-full w-80 bg-white shadow-2xl z-[55] border-l border-gray-200 flex flex-col"
                    >
                        {/* Header */}
                        <div className="p-4 bg-red-50 border-b border-red-100 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-red-700 font-bold text-lg">
                                <AlertCircle className="w-6 h-6" />
                                <span>Error ({errors.length})</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {errors.length > 0 && (
                                    <button
                                        onClick={clearErrors}
                                        className="p-1.5 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                                        title="Clear All"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                )}
                                <button
                                    onClick={toggleSidebar}
                                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-md transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Error List */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50/50">
                            {errors.length === 0 ? (
                                <div className="text-center text-gray-500 mt-10 flex flex-col items-center">
                                    <CheckCircleIcon className="w-12 h-12 text-green-500 mb-2 opacity-50" />
                                    <p>No errors found.</p>
                                    <p className="text-xs mt-1">Great job!</p>
                                </div>
                            ) : (
                                errors.map((error) => (
                                    <motion.div
                                        key={error.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        className="bg-white p-3 rounded-lg border border-red-100 shadow-sm relative group hover:shadow-md transition-shadow"
                                    >
                                        <button
                                            onClick={() => removeError(error.id)}
                                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                        <div className="pr-6">
                                            <p className="text-gray-800 text-sm leading-relaxed">{error.message}</p>
                                            <p className="text-[10px] text-gray-400 mt-2 text-right">{error.timestamp}</p>
                                        </div>
                                    </motion.div>
                                ))
                            )}
                        </div>

                        <div className="p-3 bg-gray-50 border-t border-gray-100 text-center text-xs text-gray-400">
                            Errors are stored locally for this session.
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

// Helper icon
const CheckCircleIcon = (props) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...props}
    >
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
);

export default ErrorSidebar;
